/**
 * Posture Classifier — Prompt.md §12, §13, §30.
 *
 * §30: "rule-based hôm nay, ML sau này, cùng một output shape". Vì vậy classifier là một
 * interface, và mọi thứ ngoài file này chỉ được phụ thuộc vào interface đó.
 *
 * File này KHÔNG biết `CameraPlacement`, không biết landmark, không biết baseline. Nó chỉ nhận
 * "đã lệch khỏi baseline bao nhiêu" và trả "như thế là tốt hay xấu".
 */

import type { PostureConfig, ScoreWeights } from "@studystraight/shared-config";
import type {
  IssueSeverityMap,
  PostureClassification,
  PostureIssueType,
  PostureMetrics,
} from "@studystraight/shared-types";

import { ISSUE_METRIC } from "./metrics.js";
import {
  computePostureScore,
  excessOf,
  severityOf,
  statusFromScore,
  thresholdsFor,
} from "./score.js";

/**
 * Kèm `postureScore` (khác chữ ký nháp trong plan): `status` được định nghĩa **từ** score (§17),
 * nên trả riêng hai bên buộc caller tính score hai lần trên hot path và mở đường cho việc
 * status và score nói hai chuyện khác nhau.
 */
export interface PostureJudgement extends PostureClassification {
  /** 0..100 (§17). */
  postureScore: number;
}

export interface PostureClassifier {
  classify(metrics: PostureMetrics): PostureJudgement;
}

const ISSUES = Object.keys(ISSUE_METRIC) as PostureIssueType[];

export class RuleBasedPostureClassifier implements PostureClassifier {
  constructor(
    private readonly config: PostureConfig,
    private readonly weights: ScoreWeights,
  ) {}

  classify(metrics: PostureMetrics): PostureJudgement {
    const { score, excludedMetrics } = computePostureScore(metrics, this.config, this.weights);

    const issues = Object.fromEntries(
      ISSUES.map((issue) => {
        const key = ISSUE_METRIC[issue];
        // Metric không đo được → severity NONE, KHÔNG phải "không có lỗi". Sự khác biệt sống
        // trong `excludedMetrics`, và §22 dựa vào đó để không báo cáo "0 lần" cho thứ không đo.
        if (excludedMetrics.includes(key)) return [issue, "NONE"];
        return [issue, severityOf(excessOf(key, metrics), thresholdsFor(key, this.config))];
      }),
    ) as IssueSeverityMap;

    return { status: statusFromScore(score), issues, excludedMetrics, postureScore: score };
  }
}
